import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCart } from "@/context/CartContext";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { Check, Loader2 } from "lucide-react";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", address: "", city: "", pincode: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["customer_name", "email", "phone", "address", "city", "pincode"])
      if (!form[k]) return toast.error("Please fill in all delivery fields.");
    setLoading(true);
    try {
      const { data } = await api.post("/orders", { ...form, items });
      setDone(data);
      clear();
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="pt-40 pb-32 min-h-screen bg-cream flex items-center justify-center px-6" data-testid="order-success">
        <div className="text-center max-w-lg">
          <div className="w-16 h-16 mx-auto border border-amber rounded-full flex items-center justify-center text-amber-deep"><Check className="w-7 h-7" /></div>
          <h1 className="mt-8 font-serif text-5xl text-obsidian">Order placed.</h1>
          <p className="mt-4 font-sans font-light text-obsidian/70">
            Order total <b>{inr(done.total)}</b> · Cash on Delivery. We&apos;ll call to confirm
            your delivery shortly.
          </p>
          <Link to="/shop" className="mt-8 inline-block border border-obsidian/20 px-8 py-4 font-sans uppercase tracking-[0.2em] text-xs hover:bg-obsidian hover:text-cream transition-colors">
            Continue shopping
          </Link>
        </div>
      </div>
    );

  if (items.length === 0)
    return (
      <div className="pt-40 pb-32 min-h-screen bg-cream text-center">
        <p className="font-serif text-3xl text-obsidian">Your cart is empty.</p>
        <Link to="/shop" className="mt-4 inline-block link-underline">Browse the shop</Link>
      </div>
    );

  return (
    <div className="pt-32 md:pt-40 pb-24 bg-cream min-h-screen" data-testid="checkout-page">
      <div className="mx-auto max-w-[1300px] px-5 md:px-10">
        <span className="font-sans uppercase tracking-[0.35em] text-[11px] text-amber-deep">Checkout</span>
        <h1 className="mt-5 font-serif text-5xl md:text-6xl text-obsidian">Delivery details</h1>

        <div className="mt-14 grid lg:grid-cols-[1.3fr_0.7fr] gap-12">
          <form onSubmit={submit} className="space-y-4">
            <Input data-testid="checkout-name" placeholder="Full name *" value={form.customer_name} onChange={set("customer_name")} className="rounded-none border-black/20 h-12" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input data-testid="checkout-email" type="email" placeholder="Email *" value={form.email} onChange={set("email")} className="rounded-none border-black/20 h-12" />
              <Input data-testid="checkout-phone" placeholder="Phone *" value={form.phone} onChange={set("phone")} className="rounded-none border-black/20 h-12" />
            </div>
            <Textarea data-testid="checkout-address" placeholder="Full address *" value={form.address} onChange={set("address")} className="rounded-none border-black/20 min-h-24" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input data-testid="checkout-city" placeholder="City *" value={form.city} onChange={set("city")} className="rounded-none border-black/20 h-12" />
              <Input data-testid="checkout-pincode" placeholder="Pincode *" value={form.pincode} onChange={set("pincode")} className="rounded-none border-black/20 h-12" />
            </div>
            <Textarea data-testid="checkout-notes" placeholder="Delivery notes (optional)" value={form.notes} onChange={set("notes")} className="rounded-none border-black/20 min-h-20" />
            <button type="submit" disabled={loading} data-testid="place-order-btn" className="w-full bg-obsidian text-cream py-4 font-sans uppercase tracking-[0.2em] text-xs hover:bg-amber-deep transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Place order · Cash on Delivery
            </button>
          </form>

          <div className="bg-sand p-8 h-fit">
            <h3 className="font-serif text-2xl text-obsidian">Order summary</h3>
            <div className="mt-6 space-y-4">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-4">
                  <div className="w-14 h-16 bg-cream overflow-hidden shrink-0"><img src={i.image} alt={i.name} className="w-full h-full object-cover" /></div>
                  <div className="flex-1">
                    <p className="font-serif text-lg leading-tight">{i.name}</p>
                    <p className="font-sans text-xs text-obsidian/55">Qty {i.quantity}</p>
                  </div>
                  <span className="font-sans text-sm">{inr(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-black/15 flex justify-between items-baseline">
              <span className="font-sans uppercase tracking-[0.2em] text-xs">Total</span>
              <span className="font-serif text-2xl">{inr(total)}</span>
            </div>
            <p className="mt-4 font-sans font-light text-xs text-obsidian/50">Free delivery · Pay cash on delivery</p>
          </div>
        </div>
      </div>
    </div>
  );
}
